import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  MessageSquare,
  CalendarClock,
  UserRound,
  BookOpen,
  LogOut,
  Search,
  AlertCircle,
  BadgeIndianRupee,
  ArrowRight,
  Save,
  X,
} from "lucide-react";
import api, { clearAdminToken, hasAdminToken } from "../lib/api";
import { Toaster, toast } from "sonner";

const STATUS_COLORS = {
  pending: { bg: "#fef3c7", fg: "#92400e", label: "Pending" },
  confirmed: { bg: "#dcfce7", fg: "#166534", label: "Confirmed" },
  completed: { bg: "#dbeafe", fg: "#1e40af", label: "Completed" },
  cancelled: { bg: "#fee2e2", fg: "#991b1b", label: "Cancelled" },
};

const NOTIF_COLORS = {
  sent: { bg: "#dcfce7", fg: "#166534", label: "Sent" },
  queued: { bg: "#e0e7ff", fg: "#3730a3", label: "Queued" },
  pending_configuration: { bg: "#fef3c7", fg: "#92400e", label: "Pending config" },
  failed: { bg: "#fee2e2", fg: "#991b1b", label: "Failed" },
};

function Pill({ label, tone = "neutral" }) {
  const tones = {
    neutral: { bg: "#eef2f7", fg: "#334155" },
    emergency: { bg: "#fee2e2", fg: "#991b1b" },
    handoff: { bg: "#fef3c7", fg: "#92400e" },
    pricing: { bg: "#e0e7ff", fg: "#3730a3" },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <span
      style={{
        background: t.bg,
        color: t.fg,
        padding: "3px 8px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 500,
        letterSpacing: "0.02em",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (!hasAdminToken()) {
      nav("/admin/login", { replace: true });
      return;
    }
    api.adminMe().catch(() => nav("/admin/login", { replace: true }));
  }, [nav]);

  const logout = () => {
    clearAdminToken();
    nav("/admin/login", { replace: true });
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f5f7fb", color: "#0C2747" }} data-testid="admin-dashboard">
      <Toaster position="top-right" />
      <aside
        style={{
          position: "fixed",
          left: 0, top: 0, bottom: 0,
          width: 240,
          background: "#0C2747",
          color: "#fff",
          padding: "22px 18px",
          display: "flex",
          flexDirection: "column",
          gap: 6,
          borderRight: "2px solid #C49A45",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 22 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 999,
              background: "linear-gradient(160deg, #C49A45, #e2b95b)",
              color: "#0C2747",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "'Playfair Display', serif",
              fontWeight: 700,
              fontSize: 20,
            }}
          >
            S
          </div>
          <div>
            <div className="font-display" style={{ fontSize: 18, fontWeight: 700, lineHeight: 1 }}>Srinidhi</div>
            <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>Admin dashboard</div>
          </div>
        </div>

        <NavItem id="overview" active={tab === "overview"} onClick={() => setTab("overview")} icon={<LayoutDashboard size={16} />} label="Overview" />
        <NavItem id="conversations" active={tab === "conversations"} onClick={() => setTab("conversations")} icon={<MessageSquare size={16} />} label="Conversations" />
        <NavItem id="appointments" active={tab === "appointments"} onClick={() => setTab("appointments")} icon={<CalendarClock size={16} />} label="Appointments" />
        <NavItem id="handoffs" active={tab === "handoffs"} onClick={() => setTab("handoffs")} icon={<UserRound size={16} />} label="Handoffs / leads" />
        <NavItem id="kb" active={tab === "kb"} onClick={() => setTab("kb")} icon={<BookOpen size={16} />} label="Knowledge base" />

        <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
          <Link data-testid="nav-view-chat" to="/chat" style={{ color: "#e2b95b", fontSize: 12, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}>
            <ArrowRight size={12} /> Open patient chat
          </Link>
          <button
            data-testid="admin-logout"
            onClick={logout}
            style={{ background: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,0.15)", padding: "8px 12px", borderRadius: 8, display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13 }}
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      <main style={{ marginLeft: 240, padding: "32px 40px", maxWidth: 1400 }}>
        {tab === "overview" && <OverviewTab />}
        {tab === "conversations" && <ConversationsTab />}
        {tab === "appointments" && <AppointmentsTab />}
        {tab === "handoffs" && <HandoffsTab />}
        {tab === "kb" && <KBTab />}
      </main>
    </div>
  );
}

function NavItem({ id, active, onClick, icon, label }) {
  return (
    <button
      data-testid={`admin-tab-${id}`}
      onClick={onClick}
      style={{
        background: active ? "rgba(226,185,91,0.15)" : "transparent",
        border: 0,
        color: active ? "#e2b95b" : "#dbeafe",
        padding: "10px 12px",
        borderRadius: 10,
        textAlign: "left",
        display: "flex",
        alignItems: "center",
        gap: 10,
        fontSize: 13.5,
        fontWeight: active ? 600 : 400,
        cursor: "pointer",
        borderLeft: active ? "3px solid #C49A45" : "3px solid transparent",
        transition: "background-color 160ms ease, color 160ms ease",
      }}
    >
      {icon} {label}
    </button>
  );
}

/* ------------- OVERVIEW ------------- */

function OverviewTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.analytics().then(setData).catch(() => setData(null));
  }, []);

  return (
    <div>
      <h1 className="font-display" style={{ margin: 0, fontSize: 32, fontWeight: 700 }}>Overview</h1>
      <p style={{ color: "#64748B", marginTop: 6 }}>Chat activity, appointment requests and top topics.</p>

      {!data ? (
        <div style={{ marginTop: 30, color: "#64748B" }}>Loading…</div>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginTop: 24 }}>
            <StatCard label="Conversations" value={data.totals.conversations} tone="brand" />
            <StatCard label="Appointment requests" value={data.totals.appointments} tone="gold" />
            <StatCard label="Conversion" value={`${data.conversion_rate_pct}%`} tone="brand" caption="Chats → booking requests" />
            <StatCard label="Handoffs / leads" value={data.totals.handoffs} tone="neutral" />
            <StatCard label="Emergency flags" value={data.totals.emergency_flags} tone="alert" />
            <StatCard label="Pricing asked" value={data.totals.pricing_asked} tone="neutral" />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 24, marginTop: 28 }}>
            <Card title="Last 14 days">
              {data.daily.length === 0 ? (
                <div style={{ color: "#64748B", fontSize: 13 }}>No activity yet — the chart will populate as patients start chatting.</div>
              ) : (
                <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 200 }}>
                  {data.daily.map((d) => {
                    const max = Math.max(1, ...data.daily.map((x) => Math.max(x.chats, x.bookings)));
                    return (
                      <div key={d.date} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, minWidth: 20 }}>
                        <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 160 }}>
                          <div title={`${d.chats} chats`} style={{ width: 8, background: "#1b53a6", borderRadius: 3, height: `${(d.chats / max) * 100}%`, minHeight: d.chats > 0 ? 4 : 0 }} />
                          <div title={`${d.bookings} bookings`} style={{ width: 8, background: "#C49A45", borderRadius: 3, height: `${(d.bookings / max) * 100}%`, minHeight: d.bookings > 0 ? 4 : 0 }} />
                        </div>
                        <div style={{ fontSize: 10, color: "#64748B", transform: "rotate(-45deg)", whiteSpace: "nowrap" }}>
                          {d.date.slice(5)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div style={{ display: "flex", gap: 16, marginTop: 12, fontSize: 12, color: "#475569" }}>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 10, height: 10, background: "#1b53a6", borderRadius: 3 }} /> Chats</div>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 10, height: 10, background: "#C49A45", borderRadius: 3 }} /> Booking requests</div>
              </div>
            </Card>

            <Card title="Top question topics (30 days)">
              {data.top_topics.length === 0 ? (
                <div style={{ color: "#64748B", fontSize: 13 }}>No user messages yet.</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {data.top_topics.map((t) => (
                    <div key={t.topic} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ flex: 1, fontSize: 13 }}>{t.topic}</div>
                      <div style={{ background: "#eef2f7", borderRadius: 6, flex: 2, height: 8, overflow: "hidden" }}>
                        <div
                          style={{
                            width: `${Math.min(100, (t.count / Math.max(1, data.top_topics[0].count)) * 100)}%`,
                            height: "100%",
                            background: "#1b53a6",
                          }}
                        />
                      </div>
                      <div style={{ width: 32, textAlign: "right", fontSize: 12, color: "#475569", fontVariantNumeric: "tabular-nums" }}>{t.count}</div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, tone = "neutral", caption }) {
  const tones = {
    brand: { border: "#1b53a6", bg: "#eff5ff", label: "#1b53a6" },
    gold: { border: "#C49A45", bg: "#fffaeb", label: "#92400e" },
    alert: { border: "#DC2626", bg: "#fef2f2", label: "#991b1b" },
    neutral: { border: "#e5eaf1", bg: "#ffffff", label: "#475569" },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <div
      data-testid={`stat-${label.toLowerCase().replace(/\W+/g, "-")}`}
      style={{
        background: t.bg,
        borderLeft: `4px solid ${t.border}`,
        padding: 18,
        borderRadius: 12,
        border: "1px solid #e5eaf1",
      }}
    >
      <div style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: t.label, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 30, fontWeight: 700, marginTop: 6, color: "#0C2747", fontFamily: "'Playfair Display', serif" }}>{value}</div>
      {caption && <div style={{ fontSize: 11, color: "#64748B", marginTop: 4 }}>{caption}</div>}
    </div>
  );
}

function Card({ title, children, actions }) {
  return (
    <div style={{ background: "#ffffff", border: "1px solid #e5eaf1", borderRadius: 14, padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <div style={{ fontWeight: 600, fontSize: 15 }}>{title}</div>
        {actions}
      </div>
      {children}
    </div>
  );
}

/* ------------- CONVERSATIONS ------------- */

function ConversationsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [flag, setFlag] = useState("");
  const [selected, setSelected] = useState(null);

  const load = () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (flag) params.flag = flag;
    api.listConversations(params).then((r) => { setItems(r); setLoading(false); });
  };

  useEffect(() => { load(); }, [flag]);

  return (
    <div>
      <h1 className="font-display" style={{ margin: 0, fontSize: 32, fontWeight: 700 }}>Conversations</h1>
      <p style={{ color: "#64748B", marginTop: 6 }}>Every chat session with the assistant.</p>

      <div style={{ display: "flex", gap: 10, marginTop: 20, marginBottom: 18, flexWrap: "wrap" }}>
        <div style={{ position: "relative", flex: 1, minWidth: 220 }}>
          <Search size={14} style={{ position: "absolute", left: 10, top: 11, color: "#94a3b8" }} />
          <input
            data-testid="convo-search"
            className="sd-form-input"
            placeholder="Search message content or session id…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            style={{ paddingLeft: 32, width: "100%" }}
          />
        </div>
        <select data-testid="convo-flag-filter" className="sd-form-select" value={flag} onChange={(e) => setFlag(e.target.value)}>
          <option value="">All flags</option>
          <option value="emergency">Emergency only</option>
          <option value="handoff">Handoff only</option>
          <option value="pricing_asked">Pricing asked</option>
        </select>
        <button className="sd-btn-primary" onClick={load} data-testid="convo-search-btn">Search</button>
      </div>

      <div style={{ background: "#fff", border: "1px solid #e5eaf1", borderRadius: 14, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead style={{ background: "#f8fafc", color: "#475569", textTransform: "uppercase", fontSize: 11, letterSpacing: "0.06em" }}>
            <tr>
              <th style={th}>Session</th>
              <th style={th}>Started</th>
              <th style={th}>Messages</th>
              <th style={th}>Language</th>
              <th style={th}>Flags</th>
              <th style={th}>Status</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} style={{ padding: 24, textAlign: "center", color: "#64748B" }}>Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={7} style={{ padding: 24, textAlign: "center", color: "#64748B" }}>No conversations yet.</td></tr>}
            {items.map((c) => (
              <tr key={c.session_id} data-testid={`convo-row-${c.session_id}`} style={{ borderTop: "1px solid #f1f5f9" }}>
                <td style={td}><code style={{ fontSize: 11 }}>{c.session_id.slice(0, 8)}…</code></td>
                <td style={td}>{formatTs(c.created_at)}</td>
                <td style={td}>{c.message_count}</td>
                <td style={td}>{c.language}</td>
                <td style={td}>
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                    {c.flags?.emergency && <Pill label="Emergency" tone="emergency" />}
                    {c.flags?.handoff && <Pill label="Handoff" tone="handoff" />}
                    {c.flags?.pricing_asked && <Pill label="Pricing" tone="pricing" />}
                  </div>
                </td>
                <td style={td}>
                  {c.appointment_submitted && <Pill label="Booking" tone="pricing" />}
                </td>
                <td style={td}>
                  <button data-testid={`convo-open-${c.session_id.slice(0,8)}`} className="sd-btn-ghost" style={{ padding: "4px 10px" }} onClick={() => setSelected(c.session_id)}>Open</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && <ConversationModal sessionId={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function ConversationModal({ sessionId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.getConversation(sessionId).then(setData); }, [sessionId]);

  return (
    <div
      data-testid="convo-modal"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(12,39,71,0.7)", zIndex: 100,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 14, maxWidth: 720, width: "100%",
          maxHeight: "80vh", display: "flex", flexDirection: "column",
        }}
      >
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #e5eaf1", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontWeight: 600 }}>Conversation</div>
            <code style={{ fontSize: 11, color: "#64748B" }}>{sessionId}</code>
          </div>
          <button className="sd-close-btn" style={{ color: "#0C2747" }} onClick={onClose}><X size={18} /></button>
        </div>
        <div style={{ padding: 20, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
          {!data && <div style={{ color: "#64748B" }}>Loading…</div>}
          {data && data.messages.map((m) => (
            <div key={m.id} style={{
              alignSelf: m.role === "user" ? "flex-end" : (m.role === "system" ? "center" : "flex-start"),
              maxWidth: "80%",
              background: m.role === "user" ? "#1b53a6" : (m.role === "system" ? "#fef3c7" : "#f8fafc"),
              color: m.role === "user" ? "#fff" : "#0C2747",
              padding: "8px 12px",
              borderRadius: 12,
              fontSize: 13,
              border: m.role === "assistant" ? "1px solid #e5eaf1" : "0",
              whiteSpace: "pre-wrap",
            }}>
              <div style={{ fontSize: 10, opacity: 0.7, marginBottom: 2 }}>{m.role.toUpperCase()} · {formatTs(m.created_at)}</div>
              {m.content}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ------------- APPOINTMENTS ------------- */

function AppointmentsTab() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.listAppointments(status ? { status } : {}).then((r) => { setItems(r); setLoading(false); });
  };

  useEffect(load, [status]);

  const changeStatus = async (id, s) => {
    try {
      await api.updateAppointmentStatus(id, s);
      toast.success(`Status updated to ${s}`);
      load();
    } catch (e) {
      toast.error("Could not update status");
    }
  };

  return (
    <div>
      <h1 className="font-display" style={{ margin: 0, fontSize: 32, fontWeight: 700 }}>Appointment requests</h1>
      <p style={{ color: "#64748B", marginTop: 6 }}>All booking requests submitted via the assistant. Update status once confirmed with the patient.</p>

      <div style={{ display: "flex", gap: 10, marginTop: 20, marginBottom: 18 }}>
        <select data-testid="appt-status-filter" className="sd-form-select" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="confirmed">Confirmed</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <div style={{ background: "#fff", border: "1px solid #e5eaf1", borderRadius: 14, overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 900 }}>
          <thead style={{ background: "#f8fafc", color: "#475569", textTransform: "uppercase", fontSize: 11 }}>
            <tr>
              <th style={th}>When</th>
              <th style={th}>Patient</th>
              <th style={th}>Phone</th>
              <th style={th}>Concern</th>
              <th style={th}>Preferred</th>
              <th style={th}>Email</th>
              <th style={th}>WhatsApp</th>
              <th style={th}>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} style={{ padding: 24, textAlign: "center", color: "#64748B" }}>Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={8} style={{ padding: 24, textAlign: "center", color: "#64748B" }}>No appointment requests yet.</td></tr>}
            {items.map((a) => {
              const sc = STATUS_COLORS[a.status] || STATUS_COLORS.pending;
              const ec = NOTIF_COLORS[a.notification_email_status] || NOTIF_COLORS.queued;
              const wc = NOTIF_COLORS[a.notification_whatsapp_status] || NOTIF_COLORS.queued;
              return (
                <tr key={a.id} data-testid={`appt-row-${a.id.slice(0,8)}`} style={{ borderTop: "1px solid #f1f5f9" }}>
                  <td style={td}>{formatTs(a.created_at)}</td>
                  <td style={td}>
                    <div style={{ fontWeight: 500 }}>{a.patient_name}</div>
                    {a.is_for_someone_else && (
                      <div style={{ fontSize: 11, color: "#64748B" }}>for {a.other_person_name} ({a.other_person_age})</div>
                    )}
                  </td>
                  <td style={td}><a href={`tel:+91${a.phone}`} style={{ color: "#1b53a6", textDecoration: "none" }}>+91 {a.phone}</a></td>
                  <td style={td}>{a.concern}</td>
                  <td style={td}>{a.preferred_day} · {a.preferred_time}</td>
                  <td style={td}><Pill label={ec.label} tone={a.notification_email_status === "sent" ? "pricing" : (a.notification_email_status === "failed" ? "emergency" : "handoff")} /></td>
                  <td style={td}><Pill label={wc.label} tone={a.notification_whatsapp_status === "sent" ? "pricing" : (a.notification_whatsapp_status === "failed" ? "emergency" : "handoff")} /></td>
                  <td style={td}>
                    <select
                      data-testid={`appt-status-select-${a.id.slice(0,8)}`}
                      className="sd-form-select"
                      value={a.status}
                      onChange={(e) => changeStatus(a.id, e.target.value)}
                      style={{ background: sc.bg, color: sc.fg, fontWeight: 500, borderColor: sc.bg }}
                    >
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ------------- HANDOFFS ------------- */

function HandoffsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.listHandoffs().then((r) => { setItems(r); setLoading(false); }); }, []);

  return (
    <div>
      <h1 className="font-display" style={{ margin: 0, fontSize: 32, fontWeight: 700 }}>Handoffs &amp; leads</h1>
      <p style={{ color: "#64748B", marginTop: 6 }}>Contacts collected when the assistant couldn't answer or the patient asked for a human.</p>

      <div style={{ marginTop: 20, background: "#fff", border: "1px solid #e5eaf1", borderRadius: 14, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead style={{ background: "#f8fafc", color: "#475569", textTransform: "uppercase", fontSize: 11 }}>
            <tr>
              <th style={th}>When</th>
              <th style={th}>Name</th>
              <th style={th}>Phone</th>
              <th style={th}>Message</th>
              <th style={th}>Session</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} style={{ padding: 24, textAlign: "center", color: "#64748B" }}>Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={5} style={{ padding: 24, textAlign: "center", color: "#64748B" }}>No handoffs yet.</td></tr>}
            {items.map((h) => (
              <tr key={h.id} data-testid={`handoff-row-${h.id.slice(0,8)}`} style={{ borderTop: "1px solid #f1f5f9" }}>
                <td style={td}>{formatTs(h.created_at)}</td>
                <td style={td}>{h.name}</td>
                <td style={td}><a href={`tel:+91${h.phone}`} style={{ color: "#1b53a6", textDecoration: "none" }}>+91 {h.phone}</a></td>
                <td style={{ ...td, whiteSpace: "normal", maxWidth: 360 }}>{h.message}</td>
                <td style={td}>{h.session_id ? <code style={{ fontSize: 11 }}>{h.session_id.slice(0,8)}…</code> : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ------------- KB ------------- */

function KBTab() {
  const [kb, setKb] = useState(null);
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getKB().then((r) => { setKb(r); setContent(r.content); });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.updateKB(content);
      toast.success("Knowledge base updated");
      const r = await api.getKB();
      setKb(r);
    } catch (e) {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  if (!kb) return <div style={{ color: "#64748B" }}>Loading…</div>;

  return (
    <div>
      <h1 className="font-display" style={{ margin: 0, fontSize: 32, fontWeight: 700 }}>Knowledge base</h1>
      <p style={{ color: "#64748B", marginTop: 6 }}>
        Version <strong style={{ color: "#0C2747" }}>{kb.version}</strong>
        {kb.using_override ? <span style={{ color: "#C49A45", marginLeft: 8 }}>· admin override active</span> : <span style={{ color: "#64748B", marginLeft: 8 }}>· using built-in copy</span>}
      </p>

      <div style={{ marginTop: 16, background: "#fff", border: "1px solid #e5eaf1", borderRadius: 14, padding: 16 }}>
        <textarea
          data-testid="kb-editor"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          style={{
            width: "100%",
            minHeight: 500,
            fontFamily: "Menlo, Monaco, monospace",
            fontSize: 12,
            border: "1px solid #e5eaf1",
            borderRadius: 8,
            padding: 14,
            resize: "vertical",
            color: "#0C2747",
            background: "#f8fafc",
            lineHeight: 1.5,
          }}
        />
        <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center" }}>
          <button data-testid="kb-save" className="sd-btn-primary" onClick={save} disabled={saving} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Save size={14} /> {saving ? "Saving…" : "Save changes"}
          </button>
          <div style={{ fontSize: 12, color: "#64748B" }}>{content.length.toLocaleString()} characters</div>
        </div>
      </div>
    </div>
  );
}

/* ------------- helpers ------------- */

const th = { textAlign: "left", padding: "10px 14px", fontWeight: 600, whiteSpace: "nowrap" };
const td = { padding: "12px 14px", verticalAlign: "top", whiteSpace: "nowrap" };

function formatTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: true });
  } catch { return iso; }
}
